import { ReactNode } from "react";
import { useTheme, Box } from "@mui/material";
import {Link} from "react-router-dom";

/**
 * Custom hook for Markdown rendering with highlighted quoted text
 * @returns Object containing components for ReactMarkdown
 */
export const useMarkdownRenderer = () => {
    const theme = useTheme();

    // Helper function to highlight text in quotation marks
    const highlightQuotedText = (text: string): ReactNode => {
        if (!text) return text;

        // Use a non-greedy regex to find all quoted text (both single and double quotes)
        // This regex will match text inside quotes, handling quotes that appear in the middle of text
        const regex = /("[^"]*"|'[^']*')/g;

        // Split the text by the regex to get parts that are quoted and parts that are not
        const parts = text.split(regex);

        return parts.map((part, index) => {
            // Check if this part is a quoted text (starts with a quote and ends with a quote)
            if ((part.startsWith('"') && part.endsWith('"')) || 
                (part.startsWith("'") && part.endsWith("'"))) {
                // Use theme color instead of hardcoded color
                return <Box component="span" key={index} sx={{ color: theme.palette.primary.main }}>{part}</Box>;
            }
            return part;
        });
    };

    // Custom components for ReactMarkdown
    const components = {
        // Custom renderer for paragraphs
        p: ({children}: {children?: ReactNode}) => <p>{children ? highlightQuotedText(String(children)) : null}</p>,

        // Custom renderer for headings
        h1: ({children}: {children?: ReactNode}) => <h1>{children ? highlightQuotedText(String(children)) : null}</h1>,
        h2: ({children}: {children?: ReactNode}) => <h2>{children ? highlightQuotedText(String(children)) : null}</h2>,
        h3: ({children}: {children?: ReactNode}) => <h3>{children ? highlightQuotedText(String(children)) : null}</h3>,
        h4: ({children}: {children?: ReactNode}) => <h4>{children ? highlightQuotedText(String(children)) : null}</h4>,
        h5: ({children}: {children?: ReactNode}) => <h5>{children ? highlightQuotedText(String(children)) : null}</h5>,
        h6: ({children}: {children?: ReactNode}) => <h6>{children ? highlightQuotedText(String(children)) : null}</h6>,

        // Custom renderer for emphasis (italic)
        em: ({children}: {children?: ReactNode}) => <em>{children ? highlightQuotedText(String(children)) : null}</em>,

        // Custom renderer for strong emphasis (bold)
        strong: ({children}: {children?: ReactNode}) => <strong>{children ? highlightQuotedText(String(children)) : null}</strong>,

        // Custom renderer for inline code
        code: ({children}: {children?: ReactNode}) => <code>{children ? highlightQuotedText(String(children)) : null}</code>,

        // Custom renderer for links
        a: ({children, href}: {children?: ReactNode, href?: string}) => 
            <Link to={href || ''}>{children ? highlightQuotedText(String(children)) : null}</Link>,

        // Custom renderer for list items
        li: ({children}: {children?: ReactNode}) => <li>{children ? highlightQuotedText(String(children)) : null}</li>,

        // Custom renderers for table elements
        table: ({children}: {children?: ReactNode}) => <table style={{borderCollapse: 'collapse', width: '100%'}}>{children}</table>,
        thead: ({children}: {children?: ReactNode}) => <thead>{children}</thead>,
        tbody: ({children}: {children?: ReactNode}) => <tbody>{children}</tbody>,
        tr: ({children}: {children?: ReactNode}) => <tr>{children}</tr>,
        th: ({children}: {children?: ReactNode}) => <th style={{border: '1px solid #ddd', padding: '8px', textAlign: 'left'}}>{children ? highlightQuotedText(String(children)) : null}</th>,
        td: ({children}: {children?: ReactNode}) => <td style={{border: '1px solid #ddd', padding: '8px'}}>{children ? highlightQuotedText(String(children)) : null}</td>,
    };

    return { components, highlightQuotedText };
};
